import type { ComponentType, ElementType, FC } from 'react';
import { Tag } from '@carbon/react';
import { formatNumber } from 'utils/number';
import styles from './stats-bar.module.scss';

type TagType =
  | 'red'
  | 'magenta'
  | 'purple'
  | 'blue'
  | 'cyan'
  | 'teal'
  | 'green'
  | 'gray'
  | 'cool-gray'
  | 'warm-gray'
  | 'high-contrast'
  | 'outline';

type TagSize = 'sm' | 'md' | 'lg';

interface TagProps {
  type: TagType;
  size: TagSize;
  renderIcon?: ElementType;
}

export interface StatItem {
  label: string;
  value: number;
  tagType: TagType;
  suffix?: string;
  renderIcon?: ComponentType;
  // Fraction digits for this value. Defaults to 0 (whole counts); set it for a
  // rate tile whose sub-1% value would otherwise round to "0". The color still
  // keys off the caller's raw value, so number and color stay in step.
  decimals?: number;
}

interface StatsBarProps {
  stats: StatItem[];
  size?: Extract<TagSize, 'sm' | 'md'>;
  format?: 'label-colon' | 'value-first';
}

export const StatsBar: FC<StatsBarProps> = ({
  stats,
  size = 'md',
  format = 'label-colon',
}) => (
  <div className={styles.container}>
    {stats.map(
      ({ label, value, tagType, suffix, renderIcon, decimals = 0 }) => {
        const formatted = formatNumber(
          value ?? 0,
          decimals,
          decimals,
          'decimal',
          true
        );
        const tagProps: TagProps = {
          type: tagType,
          size,
          ...(renderIcon ? { renderIcon } : {}),
        };
        return (
          <Tag key={label} {...tagProps}>
            {format === 'value-first' ? (
              <>
                {formatted} {label}
                {suffix}
              </>
            ) : (
              <>
                {label}: {formatted}
                {suffix}
              </>
            )}
          </Tag>
        );
      }
    )}
  </div>
);
